import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import {
  DetailedEventModel,
  NewEventModel,
} from "../../../data-model/eventsModal";
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import {
  EventBadgeBgColorForUI,
  EventBadgeTextColorForUI,
  EventType,
  EventTypeTextForUI,
  ExtensionEntryKind,
} from "../../../enums/eventType";
import { AuditTrialTableColumnKeys } from "../../../enums/tableColumnKeys";
import {
  capitalizeWordsSeperatedByUnderScore,
  isValidString,
} from "../../../utils/stringModifications";

export const AuditTrialTableColumnHeaders: TableHeader[] = [
  {
    label: "Timestamp",
    key: AuditTrialTableColumnKeys.TIMESTAMP,
    order: 1,
    width: 25,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Actions",
    key: AuditTrialTableColumnKeys.ACTIONS,
    order: 1,
    width: 60,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Event Type",
    key: AuditTrialTableColumnKeys.EVENT_TYPE,
    order: 3,
    width: 15,
    type: TableCellType.BADGE,
  },
];

export const AuditTrialDetailsTableColumnHeaders: TableHeader[] = [
  {
    label: "Timestamp",
    key: AuditTrialTableColumnKeys.TIMESTAMP,
    order: 1,
    width: 25,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Actions",
    key: AuditTrialTableColumnKeys.ACTIONS,
    order: 1,
    width: 60,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "",
    key: AuditTrialTableColumnKeys.VIEW_SCREENSHOT,
    order: 3,
    width: 15,
    type: TableCellType.VIEW_ICON,
  },
];

/**
 * Maps raw audit-trail events into rows for {@link AuditTrialTableColumnHeaders}.
 *
 * @param data - Audit event entries from the overview API
 * @param singleOrder - General order details used to attach the order ID to each row
 * @returns Array of row objects keyed by {@link AuditTrialTableColumnKeys}
 */
export const generateAuditTrialTableData = (
  data: NewEventModel[],
  singleOrder: GeneralOrderDetails,
) => {
  return data.map((item) => {
    return {
      [AuditTrialTableColumnKeys.TIMESTAMP]: moment(item.timestamp).format(
        "MM/DD/YYYY HH:mm:ss",
      ),
      [AuditTrialTableColumnKeys.ACTIONS]: capitalizeWordsSeperatedByUnderScore(
        checkForClickJS(item.name ?? ""),
      ),
      [AuditTrialTableColumnKeys.EVENT_TYPE]: {
        text: generateBadgeForEventType(item.tag ?? "", item.name ?? "").text,
        bgColor: generateBadgeForEventType(item.tag ?? "", item.name ?? "")
          .bgColor,
        color: generateBadgeForEventType(item.tag ?? "", item.name ?? "").color,
      },

      [AuditTrialTableColumnKeys.ROW_DATA]: {
        ...item,
        order_id: singleOrder?.order_id ?? "",
      },
    };
  });
};

/**
 * Maps a detailed audit event into exhaustive rows for
 * {@link AuditTrialDetailsTableColumnHeaders}.
 *
 * Sensitive event values (e.g. password or TOTP entries) are masked, and
 * screenshot/PDF view metadata is attached per row.
 *
 * @param data - Detailed audit event containing the individual entries
 * @returns Array of row objects keyed by {@link AuditTrialTableColumnKeys}
 */
export const generateAuditTrialExhaustiveTableData = (
  data: DetailedEventModel,
) => {
  const allEntries = data.entries ?? [];
  return allEntries.map((item) => {
    const screenshotPath = item?.metadata?.screenshot_path;
    const isQuestionEntry =
      item?.metadata?.kind === ExtensionEntryKind.QUESTION;

    /**
     * Builds the trailing detail string appended to an action label, masking
     * sensitive values such as password and TOTP inputs.
     *
     * @param item - Single detailed audit entry
     * @returns Parenthesized detail string, masked when the value is sensitive
     */
    const generateAdditionalData = (item) => {
      const eventName = item?.metadata?.name?.toLowerCase();
      if (eventName?.includes("password") || eventName?.includes("totp")) {
        return `(********)`;
      }
      if (isQuestionEntry && isValidString(item?.metadata?.answer)) {
        return `( ${item?.metadata?.answer} )`;
      }
      if (
        item?.metadata?.auth_tab_selected &&
        item?.metadata?.auth_tab_selected !== ""
      ) {
        return `( ${item?.metadata?.auth_tab_selected} )`;
      }
      if (item?.metadata?.value && item?.metadata?.value !== "") {
        return `( ${item?.metadata?.value} )`;
      }
      return "";
    };
    return {
      [AuditTrialTableColumnKeys.TIMESTAMP]: moment(item.timestamp).format(
        "MM/DD/YYYY HH:mm:ss",
      ),
      [AuditTrialTableColumnKeys.ACTIONS]:
        resolveEntryLabel(item?.metadata) + " " + generateAdditionalData(item),

      [AuditTrialTableColumnKeys.EVENT_TYPE]: {
        text: generateBadgeForEventType(
          data?.tag ?? "",
          item?.metadata?.name ?? "",
        ).text,
        bgColor: generateBadgeForEventType(
          data?.tag ?? "",
          item?.metadata?.name ?? "",
        ).bgColor,
        color: generateBadgeForEventType(
          data?.tag ?? "",
          item?.metadata?.name ?? "",
        ).color,
      },
      [AuditTrialTableColumnKeys.VIEW_SCREENSHOT]: {
        shouldHideIcon:
          screenshotPath === undefined ||
          screenshotPath === null ||
          screenshotPath === "",
        screenshotPath: screenshotPath,
        shouldShowPdfIcon:
          item?.metadata?.doc_path && item?.metadata?.doc_type !== ""
            ? true
            : false,
        pdfPath: item?.metadata?.doc_path ?? "",
        title: resolveEntryLabel(item?.metadata),
      },

      [AuditTrialTableColumnKeys.ROW_DATA]: {
        ...item,
        order_id: data.order_id,
      },
    };
  });
};

/**
 * Resolves the display label for a detailed audit entry.
 *
 * RISA-Assist question entries carry the asked question instead of the
 * `name` / `action_type` / `event_name` fields the other event sources use,
 * so they are labelled with the question text as-is.
 *
 * @param metadata - Metadata of a single detailed audit entry, may be undefined
 * @returns Human readable label, empty string when no label field is present
 */
const resolveEntryLabel = (metadata?: Record<string, any>): string => {
  if (
    metadata?.kind === ExtensionEntryKind.QUESTION &&
    isValidString(metadata?.question)
  ) {
    const question = metadata.question.trim();
    return question.charAt(0).toUpperCase() + question.slice(1);
  }
  const label =
    metadata?.name === "All"
      ? metadata?.action_type || metadata?.event_name
      : metadata?.name || metadata?.action_type || metadata?.event_name;
  return capitalizeWordsSeperatedByUnderScore(checkForClickJS(label ?? ""));
};

const generateBadgeForEventType = (event_type: string, name: string) => {
  if (event_type.includes("api") && name.includes("gather_data_from_emr")) {
    return {
      text: EventTypeTextForUI[EventType.FHIR_API_CALL],
      bgColor: EventBadgeBgColorForUI[EventType.FHIR_API_CALL],
      color: EventBadgeTextColorForUI[EventType.FHIR_API_CALL],
    };
  } else if (
    event_type.includes("api") &&
    !name.includes("gather_data_from_emr")
  ) {
    return {
      text: EventTypeTextForUI[EventType.AI_AGENT],
      bgColor: EventBadgeBgColorForUI[EventType.AI_AGENT],
      color: EventBadgeTextColorForUI[EventType.AI_AGENT],
    };
  }
  return {
    text: EventTypeTextForUI[event_type ?? ""],
    bgColor: EventBadgeBgColorForUI[event_type ?? ""],
    color: EventBadgeTextColorForUI[event_type ?? ""],
  };
};
const checkForClickJS = (event_name: string) => {
  if (event_name.includes("click_javascript")) {
    event_name = event_name.replace("click_javascript", "click_web_element");
  }
  return event_name;
};
